import { CaretDown, CaretUp } from '@tcl-boron-icons/icons';
import React from 'react';
import { flexRender, Header, Table as TanstackTable } from '@tanstack/react-table';

import type { FilterableDataTableClasses } from './tableTypes';

type CellSizingProps = {
  style?: React.CSSProperties;
};

type FilterableDataTableHeadProps<D extends object> = {
  classNameFor: (slot: keyof FilterableDataTableClasses) => string;
  table: TanstackTable<D>;
  setCellWidthAndPadding: (cellProps: CellSizingProps, columnWidth: string | number | undefined) => void;
};

function sortIcon<D extends object>(
  header: Header<D, unknown>,
  classNameFor: (slot: keyof FilterableDataTableClasses) => string,
) {
  const direction = header.column.getIsSorted();
  const Icon = direction === 'desc' ? CaretDown : CaretUp;
  if (!header.column.getCanSort() || !direction) {
    return null;
  }
  return (
    <span className={classNameFor('sortIcon')}>
      <Icon height={5} width="auto" />
    </span>
  );
}

function FilterableDataTableHead<D extends object>({
  classNameFor,
  setCellWidthAndPadding,
  table,
}: FilterableDataTableHeadProps<D>) {
  const tableName = (table.options.meta as { tableName?: string } | undefined)?.tableName ?? '';

  function renderFilter(header: Header<D, unknown>) {
    const meta = header.column.columnDef.meta;
    const Filter = meta?.filterComponent;
    const baseColumn = meta?.tableColumn;

    if (!Filter) {
      return <div className={classNameFor('filterPlaceholder')} />;
    }

    return (
      <Filter
        state={{ filters: table.getState().columnFilters }}
        setFilter={(id: string, value: unknown) => table.getColumn(id)?.setFilterValue(value)}
        table={table}
        tableName={tableName}
        column={{
          ...baseColumn,
          id: header.column.id,
          filterValue: header.column.getFilterValue(),
          setFilter: (value: unknown) => header.column.setFilterValue(value),
          preFilteredRows: table.getCoreRowModel().rows,
        }}
      />
    );
  }

  function headerCell(header: Header<D, unknown>) {
    const width = header.column.columnDef.meta?.columnWidth;
    const canSort = header.column.getCanSort() && !header.isPlaceholder;
    const onSort = canSort ? header.column.getToggleSortingHandler() : undefined;

    const thProps: React.ThHTMLAttributes<HTMLTableCellElement> = {
      className: classNameFor('th'),
      onClick: onSort,
    };

    setCellWidthAndPadding(thProps, width);

    return (
      <th {...thProps} key={header.id} colSpan={header.colSpan} rowSpan={header.rowSpan}>
        <div className={classNameFor('thContent')}>
          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
          {sortIcon(header, classNameFor)}
        </div>
        <div className={classNameFor('filter')}>
          {header.column.getCanFilter() ? renderFilter(header) : <div className={classNameFor('filterPlaceholder')} />}
        </div>
      </th>
    );
  }

  return (
    <thead className={classNameFor('thead')}>
      {table.getHeaderGroups().map((group) => (
        <tr key={group.id}>{group.headers.map(headerCell)}</tr>
      ))}
    </thead>
  );
}

export default FilterableDataTableHead;
